import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { carrello as api } from '../../api/preventivi';
import type {
  DettaglioPreventivo,
  IncollaDalCarrello,
  VoceCarrelloLetta,
} from '../../api/preventivi';
import { Icona } from '../../components/Icona';

/*  IL CARRELLO PERSONALE (Galizzi, 15/10/2026).

    «Da dentro il preventivo una funzione: mi fa selezionare le righe che voglio, metti
    nel carrello, e poi vai in un altro preventivo... di la' deve chiederti dove le vuoi,
    in quale capitolo o sottocapitolo.» Ed e' UNO PER PERSONA: in Planet il carrello era
    condiviso, e chi importava si trovava il materiale lasciato dai colleghi.

    Si mette nel carrello da qualunque preventivo, anche da uno gia' inviato — e' proprio
    da li' che si copia — e si incolla solo in uno che si puo' ancora scrivere. Il
    carrello vive sul server, quindi si ritrova chiudendo la scheda o cambiando computer.  */

interface StatoCarrello {
  voci: VoceCarrelloLetta[];
  metti: (rigaId: string) => void;
  dentro: (rigaId: string) => boolean;
}

const Contesto = createContext<StatoCarrello | null>(null);

export const useCarrello = () => useContext(Contesto);

export function FornitoreCarrello({
  preventivoId,
  children,
}: {
  preventivoId: string;
  children: (stato: {
    voci: VoceCarrelloLetta[] | null;
    aggiorna: (voci: VoceCarrelloLetta[]) => void;
    errore: string | null;
  }) => ReactNode;
}) {
  const [voci, setVoci] = useState<VoceCarrelloLetta[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    api
      .leggi()
      .then((v) => !annullato && setVoci(v))
      //  Senza permesso o senza rete il carrello semplicemente non c'e'.
      .catch(() => !annullato && setVoci(null));
    return () => {
      annullato = true;
    };
  }, []);

  const stato: StatoCarrello | null =
    voci === null
      ? null
      : {
          voci,
          metti: (rigaId) => {
            setErrore(null);
            api
              .aggiungi({ preventivoId, righe: [rigaId] })
              .then(setVoci)
              .catch((e: Error) => setErrore(e.message));
          },
          dentro: (rigaId) => voci.some((v) => v.rigaId === rigaId),
        };

  return (
    <Contesto.Provider value={stato}>{children({ voci, aggiorna: setVoci, errore })}</Contesto.Provider>
  );
}

/*  Il bottone sulla riga. Su un capitolo porta via tutto quello che ci sta sotto: e' il
    gesto che serve — «mi prendo il bruciatore» — e farlo voce per voce sarebbe venti
    clic per una decisione.  */
export function BottoneCarrello({ riga }: { riga: { id: string; descrizione: string; livello: number } }) {
  const stato = useCarrello();
  if (stato === null) return null;

  const gia = stato.dentro(riga.id);

  return (
    <button
      type="button"
      className={`carrello-riga${gia ? ' nel-carrello' : ''}`}
      aria-label={`Metti ${riga.descrizione} nel carrello`}
      title={
        gia
          ? 'Già nel carrello'
          : riga.livello < 3
            ? 'Metti nel carrello, con tutto quello che ci sta sotto'
            : 'Metti nel carrello'
      }
      disabled={gia}
      onClick={() => stato.metti(riga.id)}
    >
      <Icona nome="carrello" />
    </button>
  );
}

export function PannelloCarrello({
  preventivo: p,
  voci,
  errore: erroreFuori,
  aggiorna,
  incolla,
}: {
  preventivo: DettaglioPreventivo;
  voci: VoceCarrelloLetta[] | null;
  errore: string | null;
  aggiorna: (voci: VoceCarrelloLetta[]) => void;
  /** Assente su un preventivo che non si puo' piu' scrivere: si guarda e basta. */
  incolla?: (incolla: IncollaDalCarrello) => Promise<unknown>;
}) {
  const [aperto, setAperto] = useState(false);
  const [dove, setDove] = useState('');
  const [svuota, setSvuota] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  if (voci === null || voci.length === 0) {
    return erroreFuori ? <p className="errore">{erroreFuori}</p> : null;
  }

  /*  Dove si puo' incollare: i capitoli e i sottocapitoli di questo preventivo, in
      ordine, piu' «in fondo» per i capitoli interi. Lo stesso elenco che si vede nella
      tabella, cosi' chi sceglie riconosce i nomi.  */
  const teste = [...p.righe]
    .filter((r) => r.livello < 3)
    .sort((a, b) => a.posizione - b.posizione);

  const esistenti = voci.filter((v) => v.esiste);
  const daPreventivi = [...new Set(esistenti.map((v) => v.numeroPreventivo ?? 'senza numero'))];

  const esegui = () => {
    if (!incolla) return;
    setInCorso(true);
    setErrore(null);
    incolla({ dentroRigaId: dove === '' ? null : dove, svuota })
      .then(() => (svuota ? api.leggi().then(aggiorna) : undefined))
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  return (
    <div className="pannello-carrello">
      <p className="riassunto">
        <Icona nome="carrello" />{' '}
        <strong>
          {voci.length === 1 ? 'Una riga' : `${voci.length} righe`} nel carrello
        </strong>
        {daPreventivi.length > 0 && <> · da {daPreventivi.join(', ')}</>}{' '}
        <button type="button" className="collegamento" onClick={() => setAperto(!aperto)}>
          {aperto ? 'chiudi' : 'guarda'}
        </button>
      </p>

      {aperto && (
        <ul className="voci-carrello">
          {voci.map((v) => (
            <li key={v.id} className={v.esiste ? `livello-${v.livello}` : 'sparita'}>
              <span className="chiave">{v.numeroPreventivo ?? '—'}</span>{' '}
              {v.esiste ? (
                <>
                  {v.descrizione}
                  {v.righeSotto > 0 && <em> con {v.righeSotto} righe sotto</em>}
                </>
              ) : (
                <em>la riga non c’è più nel suo preventivo</em>
              )}
              <button
                type="button"
                className="rimuovi"
                aria-label="Togli dal carrello"
                onClick={() => api.togli(v.id).then(aggiorna)}
              >
                <Icona nome="rimuovi" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {incolla && (
        <div className="incolla">
          <label>
            Incolla
            <select value={dove} onChange={(e) => setDove(e.target.value)}>
              <option value="">in fondo, come capitoli nuovi</option>
              {teste.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.livello === 2 ? '\u00a0\u00a0\u00a0dentro ' : 'dentro '}
                  {r.descrizione || '(senza nome)'}
                </option>
              ))}
            </select>
          </label>
          <label className="spunta">
            <input type="checkbox" checked={svuota} onChange={(e) => setSvuota(e.target.checked)} />
            poi svuota il carrello
          </label>
          <button type="button" className="primario" disabled={inCorso || esistenti.length === 0} onClick={esegui}>
            {inCorso ? 'Incollo…' : 'Incolla'}
          </button>
          <button
            type="button"
            className="collegamento"
            onClick={() => api.svuota().then(() => aggiorna([]))}
          >
            svuota
          </button>
        </div>
      )}

      {(errore ?? erroreFuori) && <p className="errore">{errore ?? erroreFuori}</p>}
    </div>
  );
}
